import * as Sentry from "@sentry/nextjs";
import { ClientError, GraphQLClient, type RequestDocument } from "graphql-request";
import { markSessionExpired } from "@/lib/auth";

const ADMIN_GRAPHQL_PATH = "/api/admin/graphql";

/** Same host as relative `/api/admin/auth`, so the host-only session cookie is sent. */
const resolveAdminGraphqlEndpoint = (): string => {
  if (typeof window !== "undefined") {
    return new URL(ADMIN_GRAPHQL_PATH, window.location.origin).toString();
  }
  return ADMIN_GRAPHQL_PATH;
};

const graphqlClient = (endpoint: string) =>
  new GraphQLClient(endpoint, {
    credentials: "include",
    fetch: (input, init) => {
      const headers = new Headers(init?.headers);
      const traceData = Sentry.getTraceData();
      if (traceData["sentry-trace"]) {
        headers.set("sentry-trace", traceData["sentry-trace"]);
      }
      if (traceData.baggage) {
        headers.set("baggage", traceData.baggage);
      }

      return fetch(input, { ...init, headers, credentials: "include" });
    },
  });

export const graphqlErrorMessage = (error: unknown, fallback: string): string => {
  if (error instanceof ClientError) {
    const firstError = error.response.errors?.[0];
    if (firstError) {
      const validation = (firstError as { extensions?: { validation?: Record<string, string[]> } }).extensions
        ?.validation;
      if (validation) {
        const messages = Object.values(validation).flat().filter(Boolean);
        if (messages.length > 0) {
          return messages.join(" ");
        }
      }
      if (firstError.message) {
        return firstError.message;
      }
    }
  }
  if (error instanceof Error && error.message) {
    return error.message;
  }
  return fallback;
};

const isUnauthorizedHttpError = (error: unknown): boolean =>
  error instanceof ClientError && error.response.status === 401;

export const graphqlRequest = async <TData, TVariables extends Record<string, unknown> = Record<string, never>>(
  document: RequestDocument,
  variables?: TVariables,
): Promise<TData> => {
  const client = graphqlClient(resolveAdminGraphqlEndpoint());
  const vars: Record<string, unknown> = variables ?? {};
  try {
    return await client.request<TData>(document, vars);
  } catch (error) {
    if (isUnauthorizedHttpError(error)) {
      markSessionExpired();
    }
    throw error;
  }
};
